import { createFileRoute } from "@tanstack/react-router";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/learning-path")({
  head: () => ({
    meta: [
      { title: "Learning Path — Code Cubs Academy" },
      { name: "description", content: "From Scratch to AI — a clear roadmap for young coders." },
    ],
  }),
  component: LearningPathPage,
});

function LearningPathPage() {
  const { t, language } = useLanguage();
  const colors = ["bg-fun-yellow", "bg-fun-green", "bg-fun-blue", "bg-fun-pink"];

  const { data: dbSteps, isLoading } = useQuery({
    queryKey: ["learning-paths"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("learning_paths")
        .select("*")
        .order("step_number", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const steps =
    dbSteps && dbSteps.length > 0
      ? dbSteps.map((s, i) => ({
          n: s.step_number.toString().padStart(2, "0"),
          t: language === "ar" ? s.title_ar : s.title_en,
          d: language === "ar" ? s.description_ar : s.description_en,
          c: s.color_class || colors[i % colors.length],
        }))
      : t.path.steps.map((s, i) => ({
          ...s,
          c: colors[i % colors.length],
        }));

  return (
    <div>
      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl lg:text-6xl font-extrabold">{t.path.title}</h1>
        <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">{t.path.desc}</p>
      </section>

      <section className="container mx-auto px-4 pb-20">
        <div className="relative max-w-3xl mx-auto space-y-6">
          {isLoading && !dbSteps ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            steps.map((s, i) => (
              <div key={i} className="flex gap-5 items-start">
                <div
                  className={`shrink-0 h-20 w-20 rounded-3xl ${s.c} flex items-center justify-center font-extrabold text-2xl shadow-pop`}
                >
                  {s.n}
                </div>
                <div className="flex-1 pt-2">
                  <h2 className="font-bold text-2xl">{s.t}</h2>
                  <p className="text-muted-foreground mt-1">{s.d}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
